'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

/**
 * Social Proof Component
 *
 * Displays metrics like "X questions answered today" to build trust.
 * Used primarily in Variant B (Mobile-First Vertical).
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 *
 * ⚠️ COMPLIANCE WARNING: Default values below are placeholders for development.
 * Before going live, these MUST be replaced with real data from analytics.
 * Displaying fake metrics could be considered misleading advertising.
 */

interface SocialProofProps {
  /**
   * Questions answered count
   *
   * ⚠️ IMPORTANT: Default value (12847) is a PLACEHOLDER for development only.
   * Before launch, integrate with analytics backend to show real data.
   * Failing to do so may constitute misleading claims.
   *
   * @see packages/database/src/analytics for real metrics queries
   */
  questionsAnswered?: number;
  /** Bots created count (optional) */
  botsCreated?: number;
  /** Active users count (optional) */
  activeUsers?: number;
  /** Layout variant */
  variant?: 'minimal' | 'full';
}

export function SocialProof({
  questionsAnswered = 12847,
  botsCreated,
  activeUsers,
  variant = 'minimal',
}: SocialProofProps) {
  const t = useTranslations('creator.landing.socialProof');

  // Format number with locale-appropriate separators
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  if (variant === 'minimal') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 1.5,
          px: 2,
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
          borderRadius: 2,
        }}
      >
        <QuestionAnswerIcon
          sx={{
            fontSize: '1.25rem',
            color: 'success.main',
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: 'success.dark',
          }}
        >
          {t('questionsAnsweredToday', { count: formatNumber(questionsAnswered) })}
        </Typography>
      </Box>
    );
  }

  // Full variant with multiple metrics
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 3 },
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <MetricCard
        icon={<QuestionAnswerIcon />}
        value={formatNumber(questionsAnswered)}
        label={t('metrics.questionsAnswered')}
      />
      {botsCreated && (
        <MetricCard
          icon={<TrendingUpIcon />}
          value={formatNumber(botsCreated)}
          label={t('metrics.botsCreated')}
        />
      )}
      {activeUsers && (
        <MetricCard
          icon={<PeopleIcon />}
          value={formatNumber(activeUsers)}
          label={t('metrics.activeUsers')}
        />
      )}
    </Box>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
}

function MetricCard({ icon, value, label }: MetricCardProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 2,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
          },
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ lineHeight: 1.2 }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}
